import { useState } from 'react'
import { Navigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'

import { AppShell } from '@/components/AppShell'
import {
  AdRequestDiagnostics,
  DIAGNOSTIC_TARGETING_FIELDS,
  EMPTY_KEY_VALUE,
  type DiagnosticFormValues,
} from '@/components/diagnostics/AdRequestDiagnostics'
import { DiagnosticAdUnitPicker } from '@/components/diagnostics/DiagnosticAdUnitPicker'
import { DiagnosticLineItemPicker } from '@/components/diagnostics/DiagnosticLineItemPicker'
import { useNetwork } from '@/contexts/NetworkContext'
import { useCurrentUser } from '@/contexts/UserContext'
import { isSuperAdmin } from '@/data/superAdmins'
import { AdUnitsApi, type AdUnit } from '@/services/adUnits/adUnitsApi'
import {
  DIAGNOSTIC_REQUEST_TYPES,
  type DiagnosticKeyValue,
} from '@/services/diagnostics/diagnosticsApi'
import { LineItemsApi, type LineItem } from '@/services/lineItems/lineItemsApi'

/** The URL parameter each field of the form is kept in. */
const PARAMS = {
  requestType: 'type',
  adUnits: 'adUnit',
  lineItem: 'lineItem',
  categories: 'category',
  segments: 'segment',
  keywords: 'keyword',
  keyValues: 'kv',
  latitude: 'lat',
  longitude: 'long',
  context: 'ctx',
  ip: 'ip',
} as const

/** Key-value rows from the URL, where they are kept as JSON; one empty row when there are none. */
function parseKeyValues(raw: string | null): DiagnosticKeyValue[] {
  if (!raw) return [EMPTY_KEY_VALUE]
  try {
    const rows = JSON.parse(raw) as unknown
    if (!Array.isArray(rows)) return [EMPTY_KEY_VALUE]
    const parsed = rows
      .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
      .map((row) => ({
        key: typeof row.key === 'string' ? row.key : '',
        values: Array.isArray(row.values) ? row.values.filter((v) => typeof v === 'string') : [],
      }))
    return parsed.length ? parsed : [EMPTY_KEY_VALUE]
  } catch {
    return [EMPTY_KEY_VALUE]
  }
}

/**
 * Test ad requests against chosen ad units, to see which line items would
 * deliver and why the rest would not — UII's ad unit diagnostics tab, taken out
 * of the ad unit so any number of units can be tested at once.
 */
export function DiagnosticsPage() {
  const { t } = useTranslation()
  const currentUser = useCurrentUser()
  const { currentNetwork } = useNetwork()

  if (currentUser && !isSuperAdmin(currentUser.username)) {
    return <Navigate to="/select-module" replace />
  }

  return (
    <AppShell crumbs={[{ label: t('diagnostics.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('diagnostics.title')}</h1>
            <p>{t('diagnostics.subtitle')}</p>
          </div>
        </div>
        {/* What is picked belongs to one network, so a switch starts over. */}
        <DiagnosticsTool key={currentNetwork?.tagId ?? ''} />
      </div>
    </AppShell>
  )
}

function DiagnosticsTool() {
  const { scopeKey } = useNetwork()
  const [params, setParams] = useSearchParams()

  /**
   * Writes fields of the form to the address bar, so a set of filters can be
   * bookmarked or shared. Replaces the history entry, so Back leaves the page
   * rather than stepping through every keystroke.
   */
  const writeParams = (fields: Record<string, string | string[] | undefined>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [name, value] of Object.entries(fields)) {
          next.delete(name)
          for (const v of typeof value === 'string' ? [value] : (value ?? []))
            if (v) next.append(name, v)
        }
        return next
      },
      { replace: true }
    )

  const typeParam = params.get(PARAMS.requestType)
  const values: DiagnosticFormValues = {
    requestType: DIAGNOSTIC_REQUEST_TYPES.find((type) => type === typeParam),
    categories: params.getAll(PARAMS.categories),
    segments: params.getAll(PARAMS.segments),
    keywords: params.getAll(PARAMS.keywords),
    keyValues: parseKeyValues(params.get(PARAMS.keyValues)),
    latitude: params.get(PARAMS.latitude) ?? '',
    longitude: params.get(PARAMS.longitude) ?? '',
    context: params.get(PARAMS.context) ?? '',
    ip: params.get(PARAMS.ip) ?? '',
  }
  const setValues = (patch: Partial<DiagnosticFormValues>) =>
    writeParams(
      Object.fromEntries(
        Object.entries(patch).map(([field, value]) => {
          const name = PARAMS[field as keyof DiagnosticFormValues]
          if (field !== 'keyValues') return [name, value as string | string[] | undefined]
          const rows = value as DiagnosticKeyValue[]
          return [
            name,
            rows.length > 1 || rows.some((kv) => kv.key || kv.values.length)
              ? JSON.stringify(rows)
              : undefined,
          ]
        })
      )
    )

  // Ad units and the line item are kept in the URL by id. What was picked here
  // is already known; what came in with a link is looked up.
  const [known, setKnown] = useState<{ adUnits: AdUnit[]; lineItems: LineItem[] }>({
    adUnits: [],
    lineItems: [],
  })
  const adUnitIds = params.getAll(PARAMS.adUnits)
  const unknownAdUnitIds = adUnitIds.filter((id) => !known.adUnits.some((a) => a.id === id))
  const { data: linkedAdUnits = [], isLoading: adUnitsLoading } = useQuery({
    queryKey: ['diagnostics-linked-adunits', scopeKey, unknownAdUnitIds.join(',')],
    enabled: unknownAdUnitIds.length > 0,
    queryFn: () => AdUnitsApi.getByIds(unknownAdUnitIds),
    staleTime: Infinity,
  })
  const adUnits = adUnitIds
    .map((id) => [...known.adUnits, ...linkedAdUnits].find((adUnit) => adUnit.id === id))
    .filter((adUnit): adUnit is AdUnit => !!adUnit)
  const setAdUnits = (next: AdUnit[]) => {
    setKnown((current) => ({ ...current, adUnits: [...current.adUnits, ...next] }))
    writeParams({ [PARAMS.adUnits]: next.map((adUnit) => adUnit.id) })
  }

  const lineItemId = params.get(PARAMS.lineItem) ?? undefined
  const knownLineItem = known.lineItems.find((lineItem) => lineItem.id === lineItemId)
  const { data: linkedLineItem } = useQuery({
    queryKey: ['diagnostics-linked-line-item', scopeKey, lineItemId],
    enabled: !!lineItemId && !knownLineItem,
    queryFn: () => LineItemsApi.getById(lineItemId!),
    staleTime: Infinity,
    retry: false,
  })
  const lineItem = knownLineItem ?? (linkedLineItem?.id === lineItemId ? linkedLineItem : undefined)
  const setLineItem = (next: LineItem | undefined) => {
    if (next) setKnown((current) => ({ ...current, lineItems: [...current.lineItems, next] }))
    writeParams({ [PARAMS.lineItem]: next?.id })
  }

  return (
    <AdRequestDiagnostics
      values={values}
      onChange={setValues}
      adUnits={adUnits}
      adUnitsLoading={adUnitsLoading}
      lineItemId={lineItem?.id}
      // A link carrying targeting opens with it showing.
      targetingDefaultOpen={DIAGNOSTIC_TARGETING_FIELDS.some((field) => params.has(PARAMS[field]))}
      adUnitPicker={<DiagnosticAdUnitPicker value={adUnits} onChange={setAdUnits} />}
      lineItemPicker={<DiagnosticLineItemPicker value={lineItem} onChange={setLineItem} />}
    />
  )
}
